import {
  Badge,
  Button,
  Combobox,
  Input,
  Link,
  Option,
  Select,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  Filter20Regular,
  Search20Regular,
  ArrowClockwise24Regular,
  ChevronRight24Regular,
  ChevronDown20Regular,
  CheckmarkCircle24Regular,
  Clock24Regular,
  DismissCircle24Regular,
  Dismiss16Regular,
  ErrorCircle24Regular,
  Prohibited24Regular,
  BranchFork20Regular,
  Code20Regular,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { PageError } from '../../components/PageError/PageError.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import {
  isValidYyyyMmDd,
  useEvalRuns,
} from '../../hooks/useEvalRuns/useEvalRuns.js';
import type { EvalRunFilters } from '../../hooks/useEvalRuns/useEvalRuns.js';
import type {
  EvaluationRun,
  RunState,
} from '../../services/workflowApi.interface.js';
import { WorkflowApiService } from '../../services/workflowApi.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionScenario,
  ActionScenarioType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';

import {
  getEvalRunContext,
  getRunContextSuggestions,
  matchesRunContext,
} from './evalRunContext.js';
import { useEvalRunsPageStyles } from './EvalRunsPage.style.js';
import {
  getLoadedRunWindow,
  groupEvaluationRuns,
} from './groupEvaluationRuns.js';
import type { EvaluationRunGroup } from './groupEvaluationRuns.js';
import { RunCreator } from './RunCreator.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GROUP_BATCH_SIZE = 100;

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

interface StatusConfig {
  label: string;
  color: BadgeProps['color'];
  icon: React.ReactElement;
}

const STATUS_CONFIG: Record<RunState, StatusConfig> = {
  pending: {
    label: 'Pending',
    color: 'informative',
    icon: <Clock24Regular style={{ fontSize: 14 }} />,
  },
  running: {
    label: 'Running',
    color: 'brand',
    icon: <ArrowClockwise24Regular style={{ fontSize: 14 }} />,
  },
  completed: {
    label: 'Completed',
    color: 'success',
    icon: <CheckmarkCircle24Regular style={{ fontSize: 14 }} />,
  },
  failed: {
    label: 'Failed',
    color: 'danger',
    icon: <DismissCircle24Regular style={{ fontSize: 14 }} />,
  },
  cancelling: {
    label: 'Cancelling',
    color: 'warning',
    icon: <ErrorCircle24Regular style={{ fontSize: 14 }} />,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'informative',
    icon: <Prohibited24Regular style={{ fontSize: 14 }} />,
  },
};

const ALL_STATUSES = 'All statuses';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRangeDate(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function formatBranchLabel(branch: string, compact = false): string {
  const pullRequest = /^refs\/pull\/(\d+)\/(?:merge|head)$/.exec(branch);
  const label = pullRequest
    ? `PR #${pullRequest[1]}`
    : branch.replace(/^refs\/heads\//, '');
  if (
    !compact ||
    label.length <= 28 ||
    (branch.startsWith('refs/') && !branch.startsWith('refs/heads/'))
  )
    return label;
  return `…${label.slice(-27)}`;
}

// ---------------------------------------------------------------------------
// RunRow
// ---------------------------------------------------------------------------

interface RunRowProps {
  run: EvaluationRun;
  workspaceName: string;
  showStatus: boolean;
  showPipeline: boolean;
  showBranch: boolean;
  showCreator: boolean;
  group?: EvaluationRunGroup;
  onHistoryToggle?: (expanded: boolean) => void;
  onHistoryMore?: () => void;
}

function RunRow({
  run,
  workspaceName,
  showStatus,
  showPipeline,
  showBranch,
  showCreator,
  group,
  onHistoryToggle,
  onHistoryMore,
}: RunRowProps) {
  const styles = useEvalRunsPageStyles();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [historyLimit, setHistoryLimit] = useState(5);
  const cfg =
    run.runKind === 'external' && run.runState === 'completed'
      ? {
          ...STATUS_CONFIG.completed,
          color: 'brand' as const,
        }
      : (STATUS_CONFIG[run.runState] ?? STATUS_CONFIG.pending);
  const displayName = group?.name ?? run.displayName ?? run.id;
  const createdBy = run.systemProperties.createdBy;
  const createdDate = formatDate(run.systemProperties.createdDate);
  const isTerminal =
    run.runState === 'completed' ||
    run.runState === 'failed' ||
    run.runState === 'cancelled';
  const isSpinning =
    run.runState === 'running' || run.runState === 'cancelling';
  const ci = getEvalRunContext(run);
  const sharedCreator = group?.runs.every(
    (entry) => entry.systemProperties.createdBy === createdBy,
  );
  let buildUrl: string | undefined;
  if (ci?.buildUrl) {
    try {
      const url = new URL(ci.buildUrl);
      if (url.protocol === 'https:' && !url.username && !url.password)
        buildUrl = url.href;
    } catch {
      // Older or external records may contain an invalid URL; keep the build ID visible.
    }
  }

  const openRun = (target: EvaluationRun) => {
    void navigate(
      `/workspaces/${encodeURIComponent(workspaceName)}/evaluation/evaluation-runs/${encodeURIComponent(target.id)}/report`,
    );
  };

  return (
    <>
      <TableRow className={styles.row}>
        <TableCell className={styles.cell}>
          {isTerminal ? (
            <Button
              appearance="transparent"
              className={styles.rowName}
              onClick={() => openRun(run)}
              aria-label={`View details for ${displayName}`}
              title={displayName}
            >
              {displayName}
            </Button>
          ) : (
            <div className={styles.rowName} title="Run is still in progress">
              {displayName}
            </div>
          )}
          <div className={styles.rowMeta}>
            {group ? (
              <Button
                appearance="subtle"
                size="small"
                className={styles.historyToggle}
                icon={
                  expanded ? (
                    <ChevronDown20Regular />
                  ) : (
                    <ChevronRight24Regular />
                  )
                }
                aria-label={`Show history for ${displayName}`}
                title={`Latest run ID: ${run.id}\nCreated by: ${createdBy ?? 'Unknown'}`}
                aria-expanded={expanded}
                aria-controls={`history-${run.id}`}
                onClick={() => {
                  onHistoryToggle?.(!expanded);
                  setHistoryLimit(5);
                  setExpanded(!expanded);
                }}
              >
                {group.runs.length} {group.runs.length === 1 ? 'run' : 'runs'}{' '}
                loaded
              </Button>
            ) : (
              <span>
                {run.runKind === 'external' ? 'External result' : 'Hosted run'}
              </span>
            )}
          </div>
        </TableCell>
        {showStatus && (
          <TableCell className={mergeClasses(styles.cell, styles.statusColumn)}>
            <Badge
              className={styles.statusBadge}
              appearance="tint"
              color={cfg.color}
              size="large"
              icon={
                <span
                  className={mergeClasses(
                    styles.statusIcon,
                    isSpinning && styles.statusIconSpinning,
                  )}
                >
                  {cfg.icon}
                </span>
              }
              title={
                run.runKind === 'external' && run.runState === 'completed'
                  ? 'Result uploaded. This is not a quality pass/fail verdict.'
                  : 'Execution lifecycle, not evaluation quality.'
              }
            >
              {run.runKind === 'external' && run.runState === 'completed'
                ? 'Published'
                : cfg.label}
            </Badge>
          </TableCell>
        )}
        {showPipeline && (
          <TableCell
            className={mergeClasses(styles.cell, styles.pipelineColumn)}
          >
            <TableCellLayout
              truncate
              media={
                <Code20Regular aria-hidden className={styles.pipelineIcon} />
              }
              description={
                <div className={styles.pipelineMeta}>
                  {ci?.project && <span title={ci.project}>{ci.project}</span>}
                  {ci?.system && <span>{ci.system}</span>}
                  {ci?.buildId &&
                    (buildUrl ? (
                      <Link
                        href={buildUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open build ${ci.buildId}`}
                      >
                        #{ci.buildId}
                      </Link>
                    ) : (
                      <span>#{ci.buildId}</span>
                    ))}
                </div>
              }
            >
              <span className={styles.primaryText} title={ci.pipelineName}>
                {ci.pipelineName ??
                  (ci.buildId
                    ? 'Pipeline not recorded'
                    : run.runKind === 'hosted'
                      ? 'Hosted'
                      : 'External')}
              </span>
            </TableCellLayout>
          </TableCell>
        )}
        {showBranch && (
          <TableCell className={mergeClasses(styles.cell, styles.branchColumn)}>
            {ci.branch ? (
              <Tooltip content={ci.branch} relationship="label" withArrow>
                <Badge
                  tabIndex={0}
                  appearance="tint"
                  color="brand"
                  size="medium"
                  shape="rounded"
                  className={styles.branch}
                  icon={<BranchFork20Regular aria-hidden />}
                >
                  <span className={styles.branchText}>
                    <bdi dir="ltr">{formatBranchLabel(ci.branch, true)}</bdi>
                  </span>
                </Badge>
              </Tooltip>
            ) : (
              <span>-</span>
            )}
          </TableCell>
        )}
        <TableCell className={mergeClasses(styles.cell, styles.dateColumn)}>
          <time
            className={styles.overviewDate}
            dateTime={run.systemProperties.createdDate}
            title={run.systemProperties.createdDate}
          >
            {createdDate}
          </time>
        </TableCell>
        {showCreator && (
          <TableCell
            className={mergeClasses(styles.cell, styles.creatorColumn)}
          >
            <RunCreator createdBy={createdBy} />
          </TableCell>
        )}
      </TableRow>
      {group && expanded && (
        <TableRow>
          <TableCell
            colSpan={
              2 +
              Number(showCreator) +
              Number(showStatus) +
              Number(showPipeline) +
              Number(showBranch)
            }
            className={styles.historyCell}
          >
            <div
              id={`history-${run.id}`}
              role="region"
              aria-label={`Run history for ${group.name}`}
              className={styles.historyPanel}
            >
              {ci.pipelineName && (
                <div className={styles.compactHistoryPipeline}>
                  {ci.pipelineName}
                </div>
              )}
              <div className={styles.historyCaption}>
                <span>
                  Recent runs · Showing{' '}
                  {Math.min(historyLimit, group.runs.length)} of{' '}
                  {group.runs.length} loaded
                </span>
                {sharedCreator && <RunCreator createdBy={createdBy} />}
              </div>
              <ul className={styles.historyList}>
                {group.runs.slice(0, historyLimit).map((historyRun) => {
                  const historyContext = getEvalRunContext(historyRun);
                  const historyDate = new Date(
                    historyRun.systemProperties.createdDate,
                  );
                  return (
                    <li key={historyRun.id}>
                      <Button
                        appearance="subtle"
                        className={mergeClasses(
                          styles.historyRun,
                          sharedCreator && styles.historyRunSharedCreator,
                        )}
                        disabled={
                          !['completed', 'failed', 'cancelled'].includes(
                            historyRun.runState,
                          )
                        }
                        aria-label={`View run ${historyRun.id} for ${group.name}`}
                        title={historyRun.id}
                        onClick={() => openRun(historyRun)}
                      >
                        <span className={styles.historySummary}>
                          {(Boolean(historyContext.project) ||
                            Boolean(historyContext.system)) && (
                            <span className={styles.historyContext}>
                              {historyContext.project && (
                                <span>{historyContext.project}</span>
                              )}
                              {historyContext.system && (
                                <span>{historyContext.system}</span>
                              )}
                            </span>
                          )}
                          {(Boolean(historyContext.branch) ||
                            (historyContext.pipelineName !== ci.pipelineName &&
                              Boolean(historyContext.pipelineName))) && (
                            <span className={styles.historyContext}>
                              {historyContext.branch && (
                                <span className={styles.historyBranch}>
                                  <BranchFork20Regular
                                    aria-hidden
                                    className={styles.historyBranchIcon}
                                  />
                                  <span title={historyContext.branch}>
                                    {formatBranchLabel(historyContext.branch)}
                                  </span>
                                </span>
                              )}
                              {historyContext.pipelineName !==
                                ci.pipelineName &&
                                historyContext.pipelineName && (
                                  <span>{historyContext.pipelineName}</span>
                                )}
                            </span>
                          )}
                          <span className={styles.historyContext}>
                            <time
                              className={styles.historyDate}
                              dateTime={historyRun.systemProperties.createdDate}
                              title={formatRangeDate(
                                historyRun.systemProperties.createdDate,
                              )}
                              aria-label={formatRangeDate(
                                historyRun.systemProperties.createdDate,
                              )}
                            >
                              {historyDate.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                              {' · '}
                              {historyDate.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                timeZoneName: 'short',
                              })}
                            </time>
                            {historyContext.buildId && (
                              <span className={styles.historyBuild}>
                                Run #{historyContext.buildId}
                              </span>
                            )}
                          </span>
                        </span>
                        {!sharedCreator && (
                          <span className={styles.historyCreator}>
                            <RunCreator
                              createdBy={historyRun.systemProperties.createdBy}
                            />
                          </span>
                        )}
                        <span className={styles.historyAction}>
                          {group.runs.some(
                            (entry) => entry.runState !== 'completed',
                          ) && (
                            <span>
                              {STATUS_CONFIG[historyRun.runState].label}
                            </span>
                          )}
                          <span className={styles.historyReport}>
                            View report
                            <ChevronRight24Regular
                              aria-hidden
                              className={styles.historyReportIcon}
                            />
                          </span>
                        </span>
                      </Button>
                    </li>
                  );
                })}
              </ul>
              {historyLimit < group.runs.length && (
                <Button
                  appearance="subtle"
                  size="small"
                  className={styles.historyMore}
                  onClick={() => {
                    onHistoryMore?.();
                    setHistoryLimit((count) => count + 5);
                  }}
                >
                  Show {Math.min(5, group.runs.length - historyLimit)} older
                  runs
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface RunListProps {
  runs: EvaluationRun[];
  workspaceName: string;
  hasMore: boolean;
  onHistoryToggle: (expanded: boolean) => void;
  onHistoryMore: () => void;
}

function RunList({
  runs,
  workspaceName,
  hasMore,
  onHistoryToggle,
  onHistoryMore,
}: RunListProps) {
  const styles = useEvalRunsPageStyles();
  const listRef = useRef<HTMLDivElement>(null);
  const [listWidth, setListWidth] = useState<number>();
  useEffect(() => {
    const element = listRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0)
        setListWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const showCreator = listWidth === undefined || listWidth > 1000;
  const groups = useMemo(() => groupEvaluationRuns(runs), [runs]);
  const loadedWindow = useMemo(() => getLoadedRunWindow(runs), [runs]);
  const displayedRuns = groups.map((group) => group.latest);
  const publishedOnly = displayedRuns.every(
    (run) => run.runKind === 'external' && run.runState === 'completed',
  );
  const showPipeline =
    showCreator &&
    displayedRuns.some((run) => {
      const ci = getEvalRunContext(run);
      return [ci?.pipelineName, ci?.buildId, ci?.project, ci?.system].some(
        Boolean,
      );
    });
  const showBranch =
    (listWidth === undefined || listWidth > 640) &&
    displayedRuns.some((run) => Boolean(getEvalRunContext(run).branch));
  const rows = groups.map((group) => ({
    key: group.key,
    run: group.latest,
    group,
  }));
  return (
    <div ref={listRef} className={styles.list}>
      <div className={styles.listHeader}>
        <span>
          {`${groups.length} ${groups.length === 1 ? 'evaluation' : 'evaluations'}`}
        </span>
        <span>
          {`${groups.reduce((count, group) => count + group.runs.length, 0)} runs loaded · Latest first`}
        </span>
      </div>
      {loadedWindow && (
        <div
          role="note"
          aria-label="Loaded history date range"
          className={styles.loadedRange}
        >
          <div>
            Created between{' '}
            <time dateTime={loadedWindow.oldest}>
              {formatRangeDate(loadedWindow.oldest)}
            </time>{' '}
            and{' '}
            <time dateTime={loadedWindow.newest}>
              {formatRangeDate(loadedWindow.newest)}
            </time>
            .
          </div>
          {hasMore && <div>More history is available.</div>}
        </div>
      )}
      <Table size="medium" aria-label="Evaluations" className={styles.table}>
        <TableHeader className={styles.tableHeader}>
          <TableRow>
            <TableHeaderCell className={styles.nameColumn}>
              Evaluation
            </TableHeaderCell>
            {!publishedOnly && (
              <TableHeaderCell className={styles.statusColumn}>
                Execution status
              </TableHeaderCell>
            )}
            {showPipeline && (
              <TableHeaderCell className={styles.pipelineColumn}>
                Pipeline / build
              </TableHeaderCell>
            )}
            {showBranch && (
              <TableHeaderCell className={styles.branchColumn}>
                Branch
              </TableHeaderCell>
            )}
            <TableHeaderCell className={styles.dateColumn}>
              {publishedOnly ? 'Latest published' : 'Latest run'}
            </TableHeaderCell>
            {showCreator && (
              <TableHeaderCell className={styles.creatorColumn}>
                Created by
              </TableHeaderCell>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ key, run, group }) => (
            <RunRow
              key={key}
              run={run}
              group={group}
              workspaceName={workspaceName}
              showStatus={!publishedOnly}
              showPipeline={showPipeline}
              showBranch={showBranch}
              showCreator={showCreator}
              onHistoryToggle={onHistoryToggle}
              onHistoryMore={onHistoryMore}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvalRunsPage
// ---------------------------------------------------------------------------

export function EvalRunsPage() {
  const styles = useEvalRunsPageStyles();
  const client = useHttpClient();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const workspaceName = workspaceNameParam ?? '';
  const { activeName } = useWorkspaceContext();
  const navigate = useNavigate();
  const { account } = useAuth();
  const { recordAction } = useUserBi();

  // When the user switches workspace via the switcher while on this page,
  // redirect to the new workspace's eval-runs URL.
  useEffect(() => {
    if (activeName && workspaceName && activeName !== workspaceName) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/eval-runs`, {
        replace: true,
      });
    }
  }, [activeName, workspaceName, navigate]);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasActiveFilters = [...searchParams.keys()].some(
    (key) => key !== 'view',
  );
  const search = searchParams.get('search') ?? '';
  const dateFilter = searchParams.get('date') ?? '';
  const status = searchParams.get('status') ?? '';
  const statusFilter = Object.prototype.hasOwnProperty.call(
    STATUS_CONFIG,
    status,
  )
    ? status
    : ALL_STATUSES;
  const myRunsOnly = searchParams.get('mine') === '1';
  const requestedRange = searchParams.get('range');
  const range =
    requestedRange === 'all' ||
    requestedRange === '24h' ||
    requestedRange === '7d' ||
    requestedRange === 'custom'
      ? requestedRange
      : isValidYyyyMmDd(dateFilter)
        ? 'all'
        : '24h';
  const filters: EvalRunFilters = {
    range,
    from: searchParams.get('from') ?? '',
    to: searchParams.get('to') ?? '',
    createdBy: myRunsOnly ? (account?.username ?? '') : undefined,
    origin: searchParams.get('origin') ?? '',
    ciSystem: searchParams.get('ci') ?? '',
    project: searchParams.get('project') ?? '',
    pipeline: searchParams.get('pipeline') ?? '',
    buildId: searchParams.get('build') ?? '',
    branch: searchParams.get('branch') ?? '',
    completeWindow: range !== 'all' || isValidYyyyMmDd(dateFilter),
  };
  const [showFilters, setShowFilters] = useState(
    myRunsOnly ||
      [
        status,
        filters.origin,
        filters.ciSystem,
        filters.project,
        filters.pipeline,
        filters.buildId,
        filters.branch,
      ].some(Boolean),
  );
  const activeFilters = [
    {
      key: 'mine',
      label: 'My runs',
      value: myRunsOnly ? '1' : '',
      text: 'My runs',
    },
    {
      key: 'status',
      label: 'Status',
      value: statusFilter === ALL_STATUSES ? '' : statusFilter,
      text: statusFilter,
    },
    {
      key: 'pipeline',
      label: 'Pipeline',
      value: filters.pipeline,
      text: filters.pipeline,
    },
    {
      key: 'branch',
      label: 'Branch',
      value: filters.branch,
      text: filters.branch,
    },
    {
      key: 'build',
      label: 'Build ID',
      value: filters.buildId,
      text: filters.buildId,
    },
    {
      key: 'ci',
      label: 'CI system',
      value: filters.ciSystem,
      text: filters.ciSystem,
    },
    {
      key: 'project',
      label: 'Project',
      value: filters.project,
      text: filters.project,
    },
    {
      key: 'origin',
      label: 'Run type',
      value: filters.origin,
      text:
        filters.origin === 'external'
          ? 'External / CLI'
          : filters.origin === 'hosted'
            ? 'Hosted'
            : filters.origin,
    },
  ].filter(({ value }) => Boolean(value));

  const service = useMemo(() => new WorkflowApiService(client), [client]);
  const {
    runs,
    hasNextPage,
    isLoading,
    error,
    refresh,
    loadMore,
    isLoadingMore,
  } = useEvalRuns(
    service,
    workspaceName,
    1,
    GROUP_BATCH_SIZE,
    statusFilter,
    search,
    dateFilter,
    {
      range: filters.range,
      from: filters.from,
      to: filters.to,
      createdBy: filters.createdBy,
      origin: filters.origin,
      completeWindow: filters.completeWindow,
    },
  );
  const filteredRuns = runs.filter((run) => matchesRunContext(run, filters));
  const contextSuggestions = useMemo(
    () => getRunContextSuggestions(runs),
    [runs],
  );
  const invalidTags = runs.some((run) => getEvalRunContext(run).invalidTags);
  const hasContextFilters = [
    filters.ciSystem,
    filters.project,
    filters.pipeline,
    filters.buildId,
    filters.branch,
  ].some((value) => Boolean(value?.trim()));

  const recordControl = (control: string, active: boolean) =>
    recordAction({
      panelType: PanelType.EvaluationRunsPage,
      panelRegion: PanelRegion.Header,
      actionScenario: ActionScenario.FilterEvaluationRuns,
      actionScenarioType: ActionScenarioType.Submit,
      data: { filter: control, active },
    });
  const writeParams = (next: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    for (const [key, value] of Object.entries(next)) {
      if (value) params.set(key, value);
      else params.delete(key);
      recordControl(key, Boolean(value));
    }
    setSearchParams(params, { replace: true });
  };

  if (!workspaceName) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          Select a workspace to view evaluation runs.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {error && <PageError error={error} />}

      {/* Toolbar */}
      <div
        className={styles.toolbar}
        role="group"
        aria-label="Run search and actions"
      >
        <div className={styles.toolbarLeft}>
          <Input
            className={styles.searchInput}
            placeholder="Search runs..."
            contentBefore={<Search20Regular />}
            aria-label="Search run name or ID"
            value={search}
            onChange={(_e, d) => writeParams({ search: d.value })}
          />
          <Select
            aria-label="Created"
            value={range}
            onChange={(_e, d) =>
              writeParams({
                range: d.value,
                date: '',
                from: '',
                to: '',
              })
            }
          >
            <option value="all">All time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="custom">Custom dates (UTC)</option>
          </Select>
          <Switch
            label="My runs"
            checked={myRunsOnly}
            disabled={!account?.username}
            onChange={(_e, d) => writeParams({ mine: d.checked ? '1' : '' })}
          />
          <Button
            appearance={activeFilters.length ? 'secondary' : 'subtle'}
            className={showFilters ? styles.filtersExpanded : undefined}
            icon={<Filter20Regular />}
            aria-expanded={showFilters}
            aria-controls="eval-run-filters"
            onClick={() => {
              recordControl('moreFilters', !showFilters);
              setShowFilters(!showFilters);
            }}
          >
            {activeFilters.length
              ? `Filters (${activeFilters.length})`
              : 'Filters'}
          </Button>
          <Button
            appearance="outline"
            icon={<ArrowClockwise24Regular />}
            disabled={isLoading}
            onClick={() => {
              recordControl('refresh', true);
              refresh();
            }}
            aria-label="Refresh runs"
          >
            Refresh
          </Button>
          {dateFilter ? (
            <Button
              size="small"
              appearance="outline"
              onClick={() => writeParams({ date: '' })}
              aria-label={`Clear date filter ${dateFilter}`}
            >
              Date: {dateFilter} ✕
            </Button>
          ) : null}
        </div>
        <div className={styles.toolbarRight}>
          {hasActiveFilters && (
            <Button
              appearance="subtle"
              onClick={() => {
                recordControl('clear', false);
                setSearchParams({}, { replace: true });
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </div>
      {showFilters && (
        <div
          id="eval-run-filters"
          className={styles.filterPanel}
          role="region"
          aria-label="Run filters"
        >
          <div className={styles.filterGrid}>
            <div className={styles.filterField}>
              <label htmlFor="eval-run-status">Status</label>
              <Select
                id="eval-run-status"
                value={statusFilter}
                aria-label="Execution status"
                onChange={(_e, d) =>
                  writeParams({
                    status: d.value === ALL_STATUSES ? '' : d.value,
                  })
                }
              >
                <option>{ALL_STATUSES}</option>
                <option>pending</option>
                <option>running</option>
                <option>completed</option>
                <option>failed</option>
                <option>cancelling</option>
                <option>cancelled</option>
              </Select>
            </div>
            {(
              [
                ['pipeline', 'Pipeline name', filters.pipeline],
                ['branch', 'Branch', filters.branch],
                ['build', 'Build ID', filters.buildId],
                ['ci', 'CI system', filters.ciSystem],
                ['project', 'Project', filters.project],
              ] as const
            )
              .filter(
                ([key, , value]) =>
                  (key !== 'ci' && key !== 'project') ||
                  contextSuggestions[key].length > 0 ||
                  Boolean(value),
              )
              .map(([key, label, value]) => {
                const placeholder = `Any ${key === 'build' ? 'build ID' : key === 'ci' ? 'CI system' : key}`;
                const fieldWidth = Math.min(
                  40,
                  Math.max(
                    placeholder.length,
                    value?.length ?? 0,
                    contextSuggestions[key].reduce(
                      (longest, suggestion) =>
                        Math.max(longest, suggestion.length),
                      0,
                    ),
                  ) + 5,
                );
                const suggestions = contextSuggestions[key].filter(
                  (suggestion) =>
                    suggestion
                      .toLowerCase()
                      .includes((value ?? '').toLowerCase()),
                );
                return (
                  <div
                    key={key}
                    className={styles.filterField}
                    style={{ width: `calc(${fieldWidth}ch + 32px)` }}
                  >
                    <label htmlFor={`eval-run-${key}`}>{label}</label>
                    <Combobox
                      freeform
                      clearable
                      clearIcon={{
                        'aria-hidden': false,
                        'aria-label': `Clear ${label}`,
                        role: 'button',
                        tabIndex: 0,
                        onClick: () => writeParams({ [key]: '' }),
                        onKeyDown: (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            writeParams({ [key]: '' });
                          }
                        },
                      }}
                      listbox={{ className: styles.contextSuggestions }}
                      positioning={{
                        position: 'below',
                        align: 'start',
                        fallbackPositions: ['below-end'],
                        autoSize: false,
                        matchTargetSize: undefined,
                      }}
                      className={styles.contextCombobox}
                      input={{ className: styles.contextInput }}
                      id={`eval-run-${key}`}
                      aria-label={label}
                      placeholder={placeholder}
                      value={value ?? ''}
                      selectedOptions={value ? [value] : []}
                      onChange={(event) =>
                        writeParams({ [key]: event.target.value })
                      }
                      onOptionSelect={(_event, data) => {
                        if (data.optionValue !== undefined)
                          writeParams({ [key]: data.optionValue });
                      }}
                    >
                      {suggestions.slice(0, 8).map((suggestion) => (
                        <Option
                          key={suggestion}
                          value={suggestion}
                          text={suggestion}
                        >
                          <span
                            className={styles.suggestionText}
                            title={suggestion}
                          >
                            {suggestion}
                          </span>
                        </Option>
                      ))}
                      {suggestions.length > 8 && (
                        <Option
                          disabled
                          text={`Type to search all ${suggestions.length} values`}
                        >
                          Type to search all {suggestions.length} values
                        </Option>
                      )}
                      {suggestions.length === 0 && (
                        <Option disabled>
                          No suggestions. You can still type a value.
                        </Option>
                      )}
                    </Combobox>
                  </div>
                );
              })}
            <div className={styles.filterField}>
              <label htmlFor="eval-run-source">Run type</label>
              <Select
                id="eval-run-source"
                aria-label="Run type"
                value={filters.origin}
                onChange={(_e, d) => writeParams({ origin: d.value })}
              >
                <option value="">All types</option>
                <option value="hosted">Hosted</option>
                <option value="external">External / CLI</option>
              </Select>
            </div>
          </div>
        </div>
      )}

      {activeFilters.length > 0 && (
        <div className={styles.activeFilters} aria-label="Active filters">
          {activeFilters.map(({ key, label, value, text }) => (
            <Button
              key={key}
              size="small"
              appearance="outline"
              icon={<Dismiss16Regular />}
              iconPosition="after"
              aria-label={`Remove ${label} filter`}
              title={`${label}: ${value}`}
              onClick={() => writeParams({ [key]: '' })}
            >
              <span className={styles.filterChipText}>
                {key === 'mine' ? text : `${label}: ${text}`}
              </span>
            </Button>
          ))}
        </div>
      )}

      {range === 'custom' && (
        <div className={styles.filterRow}>
          <label className={styles.filterField} htmlFor="eval-run-from">
            From (UTC)
            <Input
              type="date"
              id="eval-run-from"
              aria-label="From (UTC)"
              value={filters.from}
              max={filters.to}
              onChange={(_e, d) => writeParams({ from: d.value })}
            />
          </label>
          <label className={styles.filterField} htmlFor="eval-run-to">
            To (UTC)
            <Input
              type="date"
              id="eval-run-to"
              aria-label="To (UTC)"
              value={filters.to}
              min={filters.from}
              onChange={(_e, d) => writeParams({ to: d.value })}
            />
          </label>
        </div>
      )}
      {(hasContextFilters || (!filters.completeWindow && showFilters)) && (
        <div role="note" className={styles.loadedRange}>
          {!filters.completeWindow &&
            'Only loaded runs are searched. Show more history to include older runs.'}
          {!isLoading && !error && hasContextFilters && (
            <div>
              {filteredRuns.length} of {runs.length} fetched runs match the
              context filters.
            </div>
          )}
        </div>
      )}

      {invalidTags && (
        <div role="alert" className={styles.loadedRange}>
          Some runs have malformed metadata tags. Only recorded string values
          can be displayed or matched by pipeline filters.
        </div>
      )}
      {isLoading ? (
        <Spinner
          label={
            filters.completeWindow
              ? range === '24h'
                ? 'Loading all runs from the last 24 hours...'
                : 'Loading all runs in the selected date range...'
              : 'Loading runs...'
          }
        />
      ) : error && runs.length === 0 ? null : filteredRuns.length === 0 ? (
        <div className={styles.emptyState}>
          {hasActiveFilters
            ? 'No runs match your current filters.'
            : 'No evaluation runs found for this workspace.'}
        </div>
      ) : (
        <RunList
          runs={filteredRuns}
          workspaceName={workspaceName}
          hasMore={hasNextPage}
          onHistoryToggle={(active) => recordControl('history', active)}
          onHistoryMore={() => recordControl('historyMore', true)}
        />
      )}

      {!filters.completeWindow && hasNextPage && (
        <Button
          className={styles.loadMore}
          appearance="subtle"
          disabled={isLoading || isLoadingMore}
          onClick={() => {
            recordControl('olderRuns', true);
            loadMore();
          }}
        >
          {isLoadingMore
            ? 'Fetching more history...'
            : `Show more history (up to ${GROUP_BATCH_SIZE} runs)`}
        </Button>
      )}
    </div>
  );
}
